import axios, { AxiosResponse } from 'axios'
import $api from '../http'
import { API_URL } from '../http'

interface RegistrationData {
  email: string
  password: string
  firstName?: string
  lastName?: string
  consent_accepted?: boolean
  consent_timestamp?: string
  [key: string]: unknown
}

// Phase 19-03 D-05 — unified return shape for AuthService.exportMyData()
// regardless of transport (streaming fetch vs ?legacy=true axios).
interface ExportMyDataResult {
  data: any
  bytes: number
}

// Phase 19-03 — extract base URL from $api defaults so streaming fetch hits
// the same endpoint as the axios instance. Empty string yields a same-origin
// relative URL (browser handles it). Defensive: fallback to API_URL constant
// then '' if defaults.baseURL is unset.
function _getApiBaseUrl(): string {
  const fromDefaults = ($api as any)?.defaults?.baseURL
  const url =
    typeof fromDefaults === 'string' && fromDefaults.length > 0
      ? fromDefaults
      : typeof API_URL === 'string'
        ? API_URL
        : ''
  return url.replace(/\/+$/, '')
}

export default class AuthService {
  static async verifyToken(): Promise<AxiosResponse> {
    return $api.get('/weresp/verify_token')
  }

  static async getAssignedVm(): Promise<AxiosResponse> {
    return $api.get('/auth/weresp/assigned_vm')
  }

  static async login(email: string, password: string): Promise<AxiosResponse> {
    return $api.post('/weresp/login', { email, password })
  }

  static async signup(
    registrationData: RegistrationData
  ): Promise<AxiosResponse> {
    return $api.post('/weresp/signup', registrationData)
  }

  static async getCountries(): Promise<AxiosResponse> {
    return $api.get('/weresp/get_countries')
  }

  static async forgotPassword(email: string): Promise<AxiosResponse> {
    return $api.post('/auth/weresp/forgot_password', { email })
  }

  static async verifyResetToken(token: string): Promise<AxiosResponse> {
    return $api.post('/auth/weresp/verify_reset_token', { token })
  }

  static async resetPassword(
    token: string,
    password: string
  ): Promise<AxiosResponse> {
    // il backend (ResetPasswordTokenRequest) vuole `new_password`
    return $api.post('/auth/weresp/reset_password', {
      token,
      new_password: password,
    })
  }

  static async getListOfAccounts(): Promise<AxiosResponse> {
    return $api.get('/weresp/get_list_of_accounts')
  }

  static async getUserProfile(): Promise<AxiosResponse> {
    return $api.get('/weresp/get_user_profile')
  }

  static async getUserModules(): Promise<AxiosResponse> {
    return $api.get('/auth/user/modules')
  }

  static async updateUserModules(
    email: string,
    modules: string[]
  ): Promise<AxiosResponse> {
    return $api.put('/auth/user/modules', { email, modules })
  }

  static async getUserAccessSummary(): Promise<AxiosResponse> {
    return $api.get('/auth/user/access-summary')
  }

  static async verifyPasswordToken(token: string): Promise<AxiosResponse> {
    return $api.get('/weresp/verify_password_token', { params: { token } })
  }

  static async confirmPassword(
    token: string,
    password: string
  ): Promise<AxiosResponse> {
    return $api.post('/weresp/confirm_password', {
      token,
      new_password: password,
    })
  }

  // Riconia il JWT a partire da uno ancora valido: non esiste un refresh token
  // separato, la rotta scambia un biglietto vivo con uno nuovo da un'ora.
  static async refreshToken(): Promise<AxiosResponse> {
    return $api.post('/auth/refresh')
  }

  // Consegna al backend un token arrivato dall'esterno (callback OAuth) e
  // riceve indietro il cookie di sessione HttpOnly. Non conia nulla: se il
  // token non e' valido la rotta risponde 401.
  static async openSession(token: string): Promise<AxiosResponse> {
    return $api.post(
      '/auth/session',
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    )
  }

  static async logout(): Promise<AxiosResponse> {
    // /auth/...: il logout legacy senza prefisso non esiste più (404 silenzioso,
    // la revoca del jti non avveniva mai) — la route viva è sotto auth_router.
    return $api.post('/auth/weresp/logout')
  }

  // ===========================================================================
  // GDPR Endpoints (Phase 19 v3.1 — extended)
  // ===========================================================================
  //
  // Phase 19-03 D-05 — server returns StreamingResponse(application/json).
  // Default path: native fetch + ReadableStream getReader() to drain bytes
  // without buffering the JSON string twice. Fallback: ?legacy=true buffered
  // axios path (D-11) for browsers without streaming support OR on streaming
  // failure.

  // GDPR: Art. 15 — Right of Access (Phase 19 streaming + agent_memories + chat_threads).
  static async exportMyData(
    opts: { legacy?: boolean } = {}
  ): Promise<ExportMyDataResult> {
    const useLegacy = opts.legacy === true

    // Legacy buffered path (D-11) — explicit opt-in OR fallback after streaming failure.
    if (useLegacy) {
      const r = await $api.get('/weresp/export_my_data?legacy=true')
      return {
        data: r.data,
        bytes:
          typeof r.data === 'string'
            ? r.data.length
            : JSON.stringify(r.data).length,
      }
    }

    // Streaming path (D-05) — native fetch + getReader.
    const url = _getApiBaseUrl() + '/weresp/export_my_data'
    let response: Response
    try {
      response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // send JWT cookie
        headers: { Accept: 'application/json' },
      })
    } catch (err) {
      // Network failure — try legacy fallback before bubbling up
      // eslint-disable-next-line no-console
      console.warn(
        '[AuthService.exportMyData] streaming fetch failed, falling back to ?legacy=true',
        err
      )
      return AuthService.exportMyData({ legacy: true })
    }

    if (!response.ok) {
      // HTTP error (401/403/429/500): match axios behavior — throw with status info
      const detail = await response.text().catch(() => '')
      const e: any = new Error(`exportMyData ${response.status}`)
      e.response = { status: response.status, data: detail }
      throw e
    }

    if (!response.body) {
      // ReadableStream not supported in this browser — fallback
      // eslint-disable-next-line no-console
      console.warn(
        '[AuthService.exportMyData] response.body absent, falling back to ?legacy=true'
      )
      return AuthService.exportMyData({ legacy: true })
    }

    // Stream-collect bytes
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let totalBytes = 0
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        totalBytes += value.byteLength
      }
    }

    // Concatenate + decode + parse
    const merged = new Uint8Array(totalBytes)
    let offset = 0
    for (const c of chunks) {
      merged.set(c, offset)
      offset += c.byteLength
    }
    const decoded = new TextDecoder('utf-8').decode(merged)
    let parsed: any
    try {
      parsed = JSON.parse(decoded)
    } catch (parseErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[AuthService.exportMyData] JSON.parse failed on streamed payload, falling back to ?legacy=true',
        parseErr
      )
      return AuthService.exportMyData({ legacy: true })
    }
    return { data: parsed, bytes: totalBytes }
  }

  // GDPR: Art. 20 — Right to Portability — downloads VTI files + metadata as ZIP
  static async exportPortability(): Promise<AxiosResponse> {
    return $api.get('/weresp/export_portability', { responseType: 'blob' })
  }

  // HIPAA Safe Harbor — de-identified portability export (VTI + metadata, PHI removed)
  static async exportPortabilityDeid(
    profile: 'minimal' | 'full' = 'full'
  ): Promise<AxiosResponse> {
    return $api.get(`/weresp/export_portability_deid?profile=${profile}`, {
      responseType: 'blob',
    })
  }

  // GDPR: Art. 17 — Right to Erasure (Phase 19-04 returns DeleteMyAccountResponse with cascade dict per D-06).
  // Request shape unchanged: server response now carries `cascade` per D-06.
  static async deleteMyAccount(
    confirmationEmail: string
  ): Promise<AxiosResponse> {
    return $api.delete('/weresp/delete_my_account', {
      data: { confirmation_email: confirmationEmail },
    })
  }

  // MFA: Setup — generates TOTP secret and QR code data URL
  static async mfaSetup(): Promise<AxiosResponse> {
    return $api.post('/auth/weresp/mfa/setup')
  }

  // MFA: Activate — confirms setup with a valid TOTP code, returns backup codes
  static async mfaActivate(
    secret: string,
    code: string
  ): Promise<AxiosResponse> {
    return $api.post('/auth/weresp/mfa/activate', { secret, code })
  }

  // MFA: Verify — validates TOTP/backup code during login challenge
  // NOTE: No auth header — uses mfa_token in body, not Bearer token
  static async mfaVerify(
    mfa_token: string,
    code: string,
    trust_device: boolean
  ): Promise<AxiosResponse> {
    return axios.post(
      `${API_URL}/auth/weresp/mfa/verify`,
      { mfa_token, code, trust_device },
      {
        withCredentials: true,
        timeout: 30000,
      }
    )
  }

  // MFA: Status — returns whether MFA is enabled and remaining backup codes
  static async mfaStatus(): Promise<AxiosResponse> {
    return $api.get('/auth/weresp/mfa/status')
  }

  // MFA: Disable — disables MFA with TOTP code and optional password
  static async mfaDisable(
    code: string,
    password?: string
  ): Promise<AxiosResponse> {
    return $api.post('/auth/weresp/mfa/disable', { code, password })
  }

  // MFA: Regenerate Backup Codes — generates new backup codes (invalidates old)
  static async mfaRegenerateBackupCodes(code: string): Promise<AxiosResponse> {
    return $api.post('/auth/weresp/mfa/regenerate-backup-codes', { code })
  }

  // Admin: Disable MFA — admin can disable MFA for any user
  static async adminDisableMfa(email: string): Promise<AxiosResponse> {
    return $api.post(`/admin/users/${encodeURIComponent(email)}/disable-mfa`)
  }
}
